import { ApolloError, gql, useMutation } from '@apollo/client';
import { DuplicateDashboardInput, Mutation } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const DUPLICATE_DASHBOARD_GQL = gql`
  mutation duplicateDashboard(
    $tenantID: ID!
    $userID: ID!
    $dashboardID: ID!
    $shouldDuplicateAnalyses: Boolean
    $scope: Scope
    $filterClause: FilterClauseInput
    $title: String
    $description: String
    $shortMetadata: String
  ) {
    duplicateDashboard(
      request: {
        tenantID: $tenantID
        userID: $userID
        dashboardID: $dashboardID
        shouldDuplicateAnalyses: $shouldDuplicateAnalyses
        scope: $scope
        filterClause: $filterClause
        title: $title
        description: $description
        shortMetadata: $shortMetadata
      }
    ) {
      dashboard {
        id
        title
        editToken
        viewToken
        version
      }
    }
  }
`;

interface UseDuplicateDashboardOptions {
  onCompleted?: (data: Mutation) => void;
  onError?: (error: ApolloError) => void;
}

export const useDuplicateDashboard = ({
  onCompleted,
  onError,
}: UseDuplicateDashboardOptions = {}) => {
  const {
    tenant: { id: tenantID },
    id: userID,
  } = useGetUser();

  const [duplicateDashboardMutation, { loading, error }] = useMutation<
    Mutation,
    DuplicateDashboardInput
  >(DUPLICATE_DASHBOARD_GQL, {
    onError,
    onCompleted,
  });

  const duplicateDashboard = useCallback(
    async (input: Omit<DuplicateDashboardInput, 'tenantID' | 'userID'>) => {
      return duplicateDashboardMutation({
        variables: {
          tenantID,
          userID,
          ...input,
        },
      });
    },
    [duplicateDashboardMutation, tenantID, userID],
  );

  return [
    duplicateDashboard,
    {
      loading,
      error,
    },
  ] as const;
};
